import { css } from '@emotion/css';
import { rotateAnimation } from 'constants/global.style';
import { colors } from 'constants/theme';
import { Game3rdTransferModel } from 'models/fundTransferModel';
import { useEffect } from 'react';
import { FaArrowDown, FaArrowUp } from 'react-icons/fa6';
import { tryImg } from 'utils/safeImage';

interface Props {
    gameData: Game3rdTransferModel;
    onLoadBalance: (action: string, providercode: string, isReload: boolean, cb?: () => void) => void;
    onActionFund: (value: string, idGame: string, cb?: () => void) => void;
}

/** Số dư hiển thị: SA và HABA trả điểm thô, các nhà cung cấp khác quy đổi theo tỉ lệ money. */
export function displayBalance(gameData: Game3rdTransferModel): number {
    const raw = Number(gameData.balance || 0);
    if (gameData.providercode === 'SA' || gameData.providercode === 'HABA') return raw;
    return raw * (gameData.money || 1);
}

export function Game3rdTransfer(props: Props) {
    const { gameData, onLoadBalance, onActionFund } = props;
    const isMaintain = Boolean(gameData.msg || gameData.message);

    useEffect(() => {
        if (!gameData.isLoaded) {
            onLoadBalance(gameData.action, gameData.providercode, false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gameData]);

    const reloadBalance = (e: any) => {
        const elTarget = e.target as HTMLElement;
        const buttonEl = elTarget.closest('button');

        if (buttonEl) {
            buttonEl.classList.add('rotating');
        }
        onLoadBalance(gameData.action, gameData.providercode, true, () => {
            if (buttonEl) {
                buttonEl.classList.remove('rotating');
            }
        });
    };

    const actionFund = (elm: any, idGame: string) => {
        onActionFund(elm, idGame);
    };

    return (
        <div className={`${styles.card} ${isMaintain ? styles.cardMaintain : ''}`}>
            <div className={styles.cardTop}>
                <img
                    className={styles.logo}
                    src={tryImg(() => require(`assets/images/fundtransfer/${gameData.providercode.toLowerCase()}_icon.png`))}
                    alt={gameData.balancename}
                />
                <button type="button" className={styles.btnRefresh} onClick={(e) => reloadBalance(e)} title="Làm mới số dư">
                    <img src={require('assets/images/fundtransfer/reload.png')} alt="" />
                </button>
            </div>

            <div className={styles.cardBody}>
                <span className={styles.balanceLabel}>Số dư</span>
                {isMaintain ? (
                    <span className={styles.msgAlert} title={gameData.msg || gameData.message}>
                        {gameData.msg || gameData.message}
                    </span>
                ) : (
                    <span className={styles.balance}>
                        {gameData.isLoaded ? new Intl.NumberFormat().format(displayBalance(gameData)) : '—'}
                    </span>
                )}
            </div>

            <div className={styles.actions}>
                <button
                    type="button"
                    className={`${styles.btnAction} ${styles.btnOut}`}
                    disabled={isMaintain}
                    onClick={() => actionFund(gameData.providercode, 'MAIN')}
                >
                    <FaArrowUp size={11} />
                    <span>Chuyển ra</span>
                </button>
                <button
                    type="button"
                    className={`${styles.btnAction} ${styles.btnIn}`}
                    disabled={isMaintain}
                    onClick={() => actionFund('MAIN', gameData.providercode)}
                >
                    <FaArrowDown size={11} />
                    <span>Chuyển vào</span>
                </button>
            </div>
        </div>
    );
}

export function MiniGameTransfer(props: any) {
    const { gameData } = props;
    return (
        <div className={styles.card}>
            <div className={styles.cardTop}>
                <span className={styles.plainName}>Mini Game</span>
                <button type="button" className={styles.btnRefresh}>
                    <img src={require('assets/images/icons/refresh.png')} alt="" />
                </button>
            </div>
            <div className={styles.cardBody}>
                <span className={styles.balanceLabel}>Số dư</span>
                {gameData.msg ? (
                    <span className={styles.msgAlert}>{gameData.msg}</span>
                ) : (
                    <span className={styles.balance}>{new Intl.NumberFormat().format(Number(gameData.balance))}</span>
                )}
            </div>
        </div>
    );
}

const LINE = '#e4e8f2';
const BRAND = '#011e98';
const INK_MUTE = '#6b7590';

const styles = {
    card: css`
        display: flex;
        flex-direction: column;
        height: 100%;
        padding: 8px 9px;
        border-radius: 10px;
        border: 1px solid ${LINE};
        background: #ffffff;
        transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease;
        &:hover {
            border-color: ${colors.blue};
            box-shadow: 0 6px 16px rgba(20, 35, 90, 0.12);
            transform: translateY(-2px);
        }
    `,
    cardMaintain: css`
        background: #f7f8fc;
        &:hover {
            transform: none;
            border-color: ${LINE};
            box-shadow: none;
        }
    `,
    cardTop: css`
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 6px;
        margin-bottom: 6px;
    `,
    logo: css`
        width: 62px;
        height: 20px;
        object-fit: contain;
        object-position: left center;
    `,
    plainName: css`
        font-size: 13px;
        font-weight: 700;
        color: ${BRAND};
    `,
    btnRefresh: css`
        flex: 0 0 auto;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 22px;
        height: 22px;
        border-radius: 50%;
        border: 1px solid ${LINE};
        background: #f5f7fc;
        cursor: pointer;
        transition: background 0.2s ease;
        &:hover {
            background: #e8ecf6;
        }
        &.rotating {
            pointer-events: none;
            animation: ${rotateAnimation} 0.8s linear infinite;
        }
        img {
            width: 11px !important;
            height: 11px;
            object-fit: contain;
        }
    `,
    cardBody: css`
        flex: 1;
        display: flex;
        align-items: baseline;
        gap: 5px;
        margin-bottom: 8px;
        min-width: 0;
    `,
    balanceLabel: css`
        flex: 0 0 auto;
        font-size: 11px;
        color: ${INK_MUTE};
    `,
    balance: css`
        flex: 1;
        min-width: 0;
        font-size: 16px;
        font-weight: 800;
        color: ${BRAND};
        font-variant-numeric: tabular-nums;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    `,
    msgAlert: css`
        flex: 1;
        min-width: 0;
        font-size: 12px;
        font-weight: 700;
        color: #d92b28;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    `,
    actions: css`
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 6px;
    `,
    btnAction: css`
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
        padding: 6px 4px;
        border-radius: 7px;
        border: 0;
        color: ${colors.white};
        font-size: 11px;
        font-weight: 700;
        white-space: nowrap;
        cursor: pointer;
        transition: filter 0.18s ease, transform 0.12s ease;
        &:hover {
            filter: brightness(1.15);
        }
        &:active {
            transform: scale(0.96);
        }
        &:disabled {
            opacity: 0.45;
            pointer-events: none;
        }
    `,
    btnOut: css`
        background: linear-gradient(180deg, #b80005, #710003);
    `,
    btnIn: css`
        background: linear-gradient(180deg, #1b53f0, #011e98);
    `,
};
